"use client"

import * as React from "react"

import { env } from "@/env.mjs"
import { posthogUiHostFor } from "@/lib/posthog-hosts.mjs"
import type { EventProperties } from "@/lib/posthog-server"

/**
 * Product analytics for the site.
 *
 * Deliberately the whole interface: wrap the tree once and every page is
 * instrumented. Nothing else in the app should import posthog-js — a custom
 * event goes through `captureEvent` below, which keeps the import dynamic.
 *
 * Renders children untouched. The tree is never wrapped, so nothing here can
 * remount the app once posthog-js arrives.
 */
export function Analytics({ children }: { children: React.ReactNode }) {
  useBootPostHog()

  return <>{children}</>
}

/**
 * Loads posthog-js after hydration, and only when a key is configured.
 *
 * The import is dynamic on purpose. `NEXT_PUBLIC_POSTHOG_KEY` is read through
 * `env.mjs`, which is an object at runtime rather than an inlined literal, so a
 * bundler cannot prove the no-key branch is dead and drop it: a static import
 * would put the whole posthog-js graph in the shared client chunk of every
 * route, evaluated on load, even in the local/preview/CI builds that have no
 * key at all. Dynamic means it is fetched off the initial route path, and in a
 * keyless build it is never fetched.
 */
function useBootPostHog() {
  const apiKey = env.NEXT_PUBLIC_POSTHOG_KEY
  const uiHost = posthogUiHostFor(env.NEXT_PUBLIC_POSTHOG_HOST)

  React.useEffect(() => {
    // No key configured (local dev, PR previews, CI) — load nothing and send
    // nothing, rather than booting a client that would queue events for
    // nowhere.
    if (!apiKey) return

    void import("posthog-js").then(({ default: posthog }) => {
      // StrictMode runs this effect twice in development.
      if (posthog.__loaded) return

      posthog.init(apiKey, {
        // The same-origin proxy from app/ingest/[...path]/route.ts, not the
        // PostHog host.
        api_host: "/ingest",
        ui_host: uiHost,
        // Pins the behaviour set instead of inheriting whatever a future
        // posthog-js release decides. Load-bearing here: it is what makes
        // `capture_pageview` default to "history_change". Without it the
        // default is capture-on-load, and the App Router navigates client-side
        // — so a visitor reading five pages would register exactly one.
        defaults: "2026-08-29",
      })
    })
  }, [apiKey, uiHost])
}

/**
 * Records a custom event on the instance `Analytics` booted.
 *
 * Not `usePostHog()` from `posthog-js/react`: that module imports posthog-js
 * statically, which would put it back in every route's shared chunk. The
 * dynamic import here resolves to the module `Analytics` already fetched.
 *
 * Drops the event when no key is configured, and when posthog-js has not
 * finished booting — a click in the first moments after hydration is not worth
 * queueing a second initialization for.
 */
export function captureEvent(
  event: string,
  properties: EventProperties,
): void {
  if (!env.NEXT_PUBLIC_POSTHOG_KEY) return

  void import("posthog-js").then(({ default: posthog }) => {
    if (posthog.__loaded) posthog.capture(event, properties)
  })
}
